import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../store/auth'

// Layout - Keep this as static import since it's used in all authenticated routes
import AuthenticatedLayout from '../components/layout/AuthenticatedLayout.vue'

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/home',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../pages/auth/Login.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/app',
    component: AuthenticatedLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/app/dashboard'
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../pages/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('../pages/auth/ProfilePage.vue'),
        meta: { requiresAuth: true }
      },
      // User management routes
      {
        path: 'users',
        name: 'UsersList',
        component: () => import('../pages/users/UsersList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      {
        path: 'users/new',
        name: 'UserForm',
        component: () => import('../pages/users/UserForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'users/:id/edit',
        name: 'UserEdit',
        component: () => import('../pages/users/UserForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'users/:id',
        name: 'ViewUser',
        component: () => import('../pages/users/UserView.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      // Company management routes
      {
        path: 'companies',
        name: 'CompaniesList',
        component: () => import('../pages/companies/CompaniesList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'companies/new',
        name: 'CompanyForm',
        component: () => import('../pages/companies/CompanyForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'companies/:id/edit',
        name: 'CompanyEdit',
        component: () => import('../pages/companies/CompanyForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Permission management routes
      {
        path: 'permissions',
        name: 'PermissionsList',
        component: () => import('../pages/permissions/PermissionsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'permissions/create',
        name: 'PermissionForm',
        component: () => import('../pages/permissions/PermissionForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'permissions/edit/:id',
        name: 'PermissionEdit',
        component: () => import('../pages/permissions/PermissionForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Location management routes
      {
        path: 'locations',
        name: 'LocationsList',
        component: () => import('../pages/locations/LocationsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      {
        path: 'locations/new',
        name: 'LocationForm',
        component: () => import('../pages/locations/LocationForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'locations/:id/edit',
        name: 'LocationEdit',
        component: () => import('../pages/locations/LocationForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'locations/:id',
        name: 'ViewLocation',
        component: () => import('../pages/locations/LocationsView.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      // Vehicle management routes
      {
        path: 'vehicles',
        name: 'VehicleTypesList',
        component: () => import('../pages/vehicles/VehicleTypesList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'vehicles/new',
        name: 'AddVehicleTypeForm',
        component: () => import('../pages/vehicles/VehicleTypeForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'vehicles/:id/edit',
        name: 'EditVehicleType',
        component: () => import('../pages/vehicles/VehicleTypeForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'tariffs',
        name: 'TariffsList',
        component: () => import('../pages/vehicles/TariffsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      {
        path: 'tariffs/new',
        name: 'AddTariffForm',
        component: () => import('../pages/vehicles/TariffForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'tariffs/:id/edit',
        name: 'EditTariff',
        component: () => import('../pages/vehicles/TariffForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'tariffs/:id',
        name: 'ViewTariff',
        component: () => import('../pages/vehicles/TariffView.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      {
        path: 'custom-tariffs',
        name: 'CustomTariffsList',
        component: () => import('../pages/vehicles/TariffsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'custom-tariffs/new',
        name: 'AddCustomTariffForm',
        component: () => import('../pages/vehicles/CustomTariffForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'custom-tariffs/:id/edit',
        name: 'EditCustomTariff',
        component: () => import('../pages/vehicles/CustomTariffForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Transaction management routes
      {
        path: 'transactions',
        name: 'TransactionsList',
        component: () => import('../pages/transactions/TransactionsList.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'transactions/:id',
        name: 'TransactionDetails',
        component: () => import('../pages/transactions/TransactionDetails.vue'),
        meta: { requiresAuth: true }
      },
      // Topup management routes
      {
        path: 'topups',
        name: 'TopupsList',
        component: () => import('../pages/topups/TopupsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Loyalty Cards management routes
      {
        path: 'loyalty-cards',
        name: 'LoyaltyCards',
        component: () => import('../pages/loyalty-cards/LoyaltyCards.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'loyalty-card-requests',
        name: 'LoyaltyCardRequests',
        component: () => import('../pages/loyalty-cards/LoyaltyCardRequests.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'loyalty-card-requests/:id',
        name: 'ViewLoyaltyCardRequest',
        component: () => import('../pages/loyalty-cards/ViewLoyaltyCardRequest.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // NFC Management routes
      {
        path: 'nfc-cards',
        name: 'NfcCards',
        component: () => import('../pages/nfc/NfcCards.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      {
        path: 'card-subscriptions',
        name: 'CardSubscriptions',
        component: () => import('../pages/nfc/CardSubscriptions.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      // Subscriptions management routes
      {
        path: 'subscriptions',
        name: 'SubscriptionList',
        component: () => import('../pages/subscriptions/SubscriptionList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'subscriptions/new',
        name: 'AddSubscription',
        component: () => import('../pages/subscriptions/SubscriptionForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'subscriptions/:id/edit',
        name: 'EditSubscription',
        component: () => import('../pages/subscriptions/SubscriptionForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'subscriptions/:id',
        name: 'ViewSubscriptionDetails',
        component: () => import('../pages/subscriptions/SubscriptionView.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Device management routes
      {
        path: 'devices',
        name: 'DevicesList',
        component: () => import('../pages/devices/DevicesList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor'] }
      },
      {
        path: 'devices/new',
        name: 'DeviceForm',
        component: () => import('../pages/devices/DeviceForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'devices/:id/edit',
        name: 'DeviceEdit',
        component: () => import('../pages/devices/DeviceForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Contractor management routes
      {
        path: 'contractors',
        name: 'ContractorsList',
        component: () => import('../pages/contractors/ContractorsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator'] }
      },
      {
        path: 'contractors/new',
        name: 'ContractorForm',
        component: () => import('../pages/contractors/contractorForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator'] }
      },
      {
        path: 'contractors/:id',
        name: 'ContractorsView',
        component: () => import('../pages/contractors/ContractorsView.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator'] }
      },
      {
        path: 'contractors/:id/edit',
        name: 'ContractorEdit',
        component: () => import('../pages/contractors/contractorForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator'] }
      },
      // Reports routes
      {
        path: 'reports',
        name: 'ReportsPage',
        component: () => import('../pages/reports/ReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor', 'Finance'] }
      },
      {
        path: 'reports/pdf',
        name: 'PdfExport',
        component: () => import('../pages/reports/PdfExport.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor', 'Finance'] }
      },
      {
        path: 'reports/excel',
        name: 'ExcelExport',
        component: () => import('../pages/reports/ExcelExport.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor', 'Finance'] }
      },
      // Settings routes
      {
        path: 'settings',
        name: 'SettingsPage',
        component: () => import('../pages/settings/SettingsPage.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor', 'Finance'] }
      },
      // Accounting routes (PremierAdmin & SuperAdmin only)
      {
        path: 'accounting',
        name: 'AccountingDashboard',
        component: () => import('../pages/accounting/AccountingDashboard.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/suppliers',
        name: 'SuppliersList',
        component: () => import('../pages/accounting/Suppliers/SuppliersList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/suppliers/new',
        name: 'CreateSupplier',
        component: () => import('../pages/accounting/Suppliers/SupplierForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/suppliers/:id',
        name: 'ViewSupplier',
        component: () => import('../pages/accounting/Suppliers/ViewSupplier.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/suppliers/:id/edit',
        name: 'EditSupplier',
        component: () => import('../pages/accounting/Suppliers/SupplierForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Stock Items routes
      {
        path: 'accounting/stock-items',
        name: 'StockItemsList',
        component: () => import('../pages/accounting/Stock/StockItemsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-items/new',
        name: 'CreateStockItem',
        component: () => import('../pages/accounting/Stock/StockItemForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-items/:id',
        name: 'ViewStockItem',
        component: () => import('../pages/accounting/Stock/ViewStockItem.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-items/:id/edit',
        name: 'EditStockItem',
        component: () => import('../pages/accounting/Stock/StockItemForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-batches',
        name: 'StockBatchesList',
        component: () => import('../pages/accounting/Stock/StockBatchesList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-batches/:id',
        name: 'ViewStockBatch',
        component: () => import('../pages/accounting/Stock/ViewStockBatch.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-logs',
        name: 'StockLogsList',
        component: () => import('../pages/accounting/Stock/StockLogsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-logs/:id',
        name: 'ViewStockLog',
        component: () => import('../pages/accounting/Stock/ViewStockLog.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Purchase Orders routes
      {
        path: 'accounting/purchase-orders',
        name: 'PurchaseOrdersList',
        component: () => import('../pages/accounting/PurchaseOrders/PurchaseOrdersList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/purchase-orders/new',
        name: 'CreatePurchaseOrder',
        component: () => import('../pages/accounting/PurchaseOrders/PurchaseOrderForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/purchase-orders/:id',
        name: 'ViewPurchaseOrder',
        component: () => import('../pages/accounting/PurchaseOrders/ViewPurchaseOrder.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/purchase-orders/:id/edit',
        name: 'EditPurchaseOrder',
        component: () => import('../pages/accounting/PurchaseOrders/PurchaseOrderForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/purchase-orders/:id/receive',
        name: 'ReceiveStock',
        component: () => import('../pages/accounting/PurchaseOrders/ReceiveStock.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Quotations routes
      {
        path: 'accounting/quotations',
        name: 'QuotationsList',
        component: () => import('../pages/accounting/Quotations/QuotationsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/quotations/new',
        name: 'CreateQuotation',
        component: () => import('../pages/accounting/Quotations/QuotationForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/quotations/:id',
        name: 'ViewQuotation',
        component: () => import('../pages/accounting/Quotations/ViewQuotation.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/quotations/:id/edit',
        name: 'EditQuotation',
        component: () => import('../pages/accounting/Quotations/QuotationForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Invoices routes
      {
        path: 'accounting/invoices',
        name: 'InvoicesList',
        component: () => import('../pages/accounting/Invoices/InvoicesList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/invoices/new',
        name: 'CreateInvoice',
        component: () => import('../pages/accounting/Invoices/InvoiceForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/invoices/:id',
        name: 'ViewInvoice',
        component: () => import('../pages/accounting/Invoices/ViewInvoice.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/invoices/:id/edit',
        name: 'EditInvoice',
        component: () => import('../pages/accounting/Invoices/InvoiceForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/invoices/:id/payment',
        name: 'RecordPayment',
        component: () => import('../pages/accounting/Invoices/RecordPayment.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Tax Settings
      {
        path: 'accounting/tax-settings',
        name: 'TaxSettings',
        component: () => import('../pages/accounting/Tax/TaxSettings.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Quality Standards
      {
        path: 'accounting/quality-standards',
        name: 'QualityStandards',
        component: () => import('../pages/accounting/Quality/QualityStandards.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Stock Adjustments
      {
        path: 'accounting/stock-adjustments',
        name: 'StockAdjustmentsList',
        component: () => import('../pages/accounting/Adjustments/StockAdjustmentsList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-adjustments/new',
        name: 'CreateStockAdjustment',
        component: () => import('../pages/accounting/Adjustments/StockAdjustmentForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/stock-adjustments/:id',
        name: 'ViewStockAdjustment',
        component: () => import('../pages/accounting/Adjustments/ViewStockAdjustment.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      // Payment Plans
      {
        path: 'accounting/payment-plans',
        name: 'PaymentPlansList',
        component: () => import('../pages/accounting/PaymentPlans/PaymentPlansList.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/payment-plans/new',
        name: 'CreatePaymentPlan',
        component: () => import('../pages/accounting/PaymentPlans/PaymentPlanForm.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      },
      {
        path: 'accounting/payment-plans/:id',
        name: 'ViewPaymentPlan',
        component: () => import('../pages/accounting/PaymentPlans/ViewPaymentPlan.vue'),
        meta: { requiresAuth: true, roles: ['PremierAdmin', 'SuperAdmin'] }
      }
    ]
  },
  // 404 page
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../pages/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const isAuthenticated = authStore.isAuthenticated
  const userRole = authStore.user?.role

  // Allow public routes (like landing page)
  if (to.meta.isPublic) {
    next()
    return
  }

  // Handle guest-only routes (like login)
  if (to.meta.requiresGuest) {
    if (isAuthenticated) {
      next('/app/dashboard')
    } else {
      next()
    }
    return
  }

  // Handle protected routes
  if (to.meta.requiresAuth) {
    if (!isAuthenticated) {
      next('/login')
      return
    }

    // Check role-based access
    if (to.meta.roles && !to.meta.roles.includes(userRole)) {
      next('/app/dashboard')
      return
    }
  }

  next()
})

export default router
